import React, { useMemo, useState } from "react";
import { X, Users, Truck, Search } from "lucide-react";

export default function AssignModal({
  show,
  onClose,
  teams,
  assignTab,
  setAssignTab,
  selectedAssignTeam,
  setSelectedAssignTeam,
  teamDrivers,
  allTeamDrivers,
  selectedAgentId,
  setSelectedAgentId,
  handleAssignTask,
}) {
  const [driverSearch, setDriverSearch] = useState("");

  const filteredDrivers = useMemo(() => {
    if (assignTab === "team") {
      return teamDrivers || [];
    }

    const allDrivers = allTeamDrivers || [];
    const query = driverSearch.trim().toLowerCase();

    // Global search across all teams / all drivers
    if (query) {
      return allDrivers.filter((driver) => {
        const name = String(driver.fullName || "").toLowerCase();
        const id = String(driver.driverId || "").toLowerCase();
        const dutyStatus = String(driver.dutyStatus || "").toLowerCase();
        const teamName = String(
          driver.team_name || driver.teamName || driver.team || ""
        ).toLowerCase();

        return (
          name.includes(query) ||
          id.includes(query) ||
          dutyStatus.includes(query) ||
          teamName.includes(query)
        );
      });
    }

    // Team filter only when search is empty
    if (selectedAssignTeam) {
      return allDrivers.filter((driver) => {
        const teamName = driver.team_name || driver.teamName || driver.team;
        return teamName === selectedAssignTeam;
      });
    }

    return allDrivers;
  }, [
    assignTab,
    teamDrivers,
    allTeamDrivers,
    driverSearch,
    selectedAssignTeam,
  ]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/45 px-4 py-6">
      <div className="relative z-[100000] flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-2xl">
        {/* Header */}
    <div className="flex shrink-0 items-center justify-between bg-orange-400 px-4 py-3 text-white sm:px-6 sm:py-4">
          <div>
            <h2 className="text-xl font-bold ">Assign Driver</h2>
            <p className="text-sm text-white-500">
              Select a team or choose a driver to assign this task
            </p>
          </div>

          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-orange-50 hover:text-orange-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs + Search */}
        <div className="shrink-0 px-4 pt-4 sm:px-6 sm:pt-5">
          <div className="inline-flex rounded-xl border border-orange-100 bg-white p-1">
            <button
              onClick={() => setAssignTab("team")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                assignTab === "team"
                  ? "bg-orange-400 text-white"
                  : "text-orange-400 hover:bg-orange-50"
              }`}
            >
              <Users size={16} />
              Team Members
            </button>

            <button
              onClick={() => setAssignTab("all")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                assignTab === "all"
                  ? "bg-orange-400 text-white"
                  : "text-orange-400 hover:bg-orange-50"
              }`}
            >
              <Truck size={16} />
              All Drivers
            </button>
          </div>

          {assignTab === "all" && (
            <div className="mt-4">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  placeholder="Search any driver by name, ID, status, or team..."
                  className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-gray-700 outline-none transition focus:border-orange-400"
                />
              </div>

              {driverSearch.trim() && (
                <p className="mt-2 text-xs font-medium text-orange-400">
                  Showing search results from all teams
                </p>
              )}
            </div>
          )}
        </div>

        {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
  <div className="flex min-h-0 flex-col gap-4">
            {assignTab === "all" && (
              <div className="flex-shrink-0">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Select Team
                  </h3>
                  {selectedAssignTeam && !driverSearch.trim() && (
                    <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-500">
                      Selected: {selectedAssignTeam}
                    </span>
                  )}
                </div>

               <div className="grid max-h-[140px] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:max-h-44 sm:grid-cols-2 lg:grid-cols-3">
                  {(teams || []).map((t, idx) => {
                    const isSelected =
                      !driverSearch.trim() &&
                      selectedAssignTeam === t.team_name;

                    return (
                      <button
                        key={`${t.team_name}-${idx}`}
                        onClick={() => setSelectedAssignTeam(t.team_name)}
                        className={`rounded-xl border p-4 text-left transition-all ${
                          isSelected
                            ? "border-orange-400 bg-orange-50"
                            : "border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50"
                        } ${driverSearch.trim() ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`font-semibold ${
                                isSelected ? "text-orange-500" : "text-gray-800"
                              }`}
                            >
                              {t.team_name}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {driverSearch.trim()
                                ? "Search is showing all teams"
                                : "Click to view drivers"}
                            </p>
                          </div>

                          <div
                            className={`h-3 w-3 rounded-full ${
                              isSelected ? "bg-orange-400" : "bg-gray-300"
                            }`}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

         <div className="flex min-h-[260px] flex-col">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">
                  Available Drivers
                </h3>

                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {(filteredDrivers || []).length} Driver
                  {(filteredDrivers || []).length !== 1 ? "s" : ""}
                </span>
              </div>

         <div className="max-h-[42vh] overflow-y-auto pr-1 sm:max-h-[46vh]">
                {filteredDrivers && filteredDrivers.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {filteredDrivers.map((driver, idx) => {
                      const isOffline = driver.dutyStatus === "offline";
                      const isSelected = selectedAgentId === driver.driverId;

                      return (
                        <button
                          key={driver.driverId || idx}
                          type="button"
                          onClick={() => {
                            if (isOffline) return;
                            setSelectedAgentId(driver.driverId);
                          }}
                          disabled={isOffline}
                          className={`rounded-xl border p-3 text-left transition-all ${
                            isSelected
                              ? "border-orange-400 bg-orange-50 ring-1 ring-orange-200"
                              : "border-gray-200 bg-white hover:border-orange-200"
                          } ${
                            isOffline
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-800">
                                {driver.fullName || "Unnamed Driver"}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                ID: {driver.driverId || "--"}
                              </p>
                              {(driver.team_name ||
                                driver.teamName ||
                                driver.team) && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Team:{" "}
                                  {driver.team_name ||
                                    driver.teamName ||
                                    driver.team}
                                </p>
                              )}
                            </div>

                            <span
                              className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
                                isOffline
                                  ? "bg-red-100 text-red-600"
                                  : "bg-emerald-100 text-emerald-600"
                              }`}
                            >
                              {driver.dutyStatus || "unknown"}
                            </span>
                          </div>

                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {isOffline ? "Driver is offline" : "Tap to assign"}
                            </span>

                            {isSelected && (
                              <span className="rounded-full bg-orange-400 px-2.5 py-1 text-[11px] font-semibold text-white">
                                Selected
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-orange-200 bg-orange-50/40 text-center">
                    <div className="rounded-full bg-orange-100 p-4">
                      <Users className="text-orange-400" size={24} />
                    </div>
                    <p className="mt-4 text-sm font-semibold text-gray-700">
                      No drivers available
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Try selecting another team or refining your search
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
      <div className="shrink-0 flex items-center justify-end gap-3 border-t border-orange-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <button
            onClick={onClose}
            className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleAssignTask}
            disabled={!selectedAgentId}
            className="rounded-xl bg-orange-400 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Assign Driver
          </button>
        </div>
      </div>
    </div>
  );
}